import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Input, Label, Row, ButtonGroup } from 'reactstrap';
import FileHelper from '../../helpers/FileHelper';
import TextPhotoDefaults from '../../enums/TextPhotoDefaults';
import CanvasHelper from '../../helpers/CanvasHelper';
import Strings from '../../translations/Translations';
import LayerIdGenerator from '../../helpers/LayerIdGenerator';
import Slider from 'rc-slider';
import _ from 'lodash';

const CANVAS_DEFAULT_WIDTH = TextPhotoDefaults.CANVAS_DEFAULT_WIDTH;
const CANVAS_DEFAULT_HEIGHT = TextPhotoDefaults.CANVAS_DEFAULT_HEIGHT;
const CANVAS_DEFAULT_BACKGROUND_COLOR = TextPhotoDefaults.CANVAS_DEFAULT_BACKGROUND_COLOR;

export default function TextPhoto(props) {
  const { parentSaveCallback, editing, toggle } = props;
  const canvasRef = useRef(null);
  const filesInput = useRef(null);

  const [layers, setLayers] = useState([]);
  const [renderedImage, setRenderedImage] = useState();

  const draw = useCallback(async (items) => {
    CanvasHelper.clear({ canvas: canvasRef.current });

    if (items && items.length > 0) {
      for (const item of items) {
        if (item && item !== null) {
          if (item.type) {
            if (item.type === 'image') {
              await CanvasHelper.addImage({ canvas: canvasRef.current, imageDataURI: item.dataUri })
            } else if (item.type === 'shadow') {
              CanvasHelper.addBackgroundColor({ canvas: canvasRef.current, color: item.color });
            } else if (item.type === 'text') {
              const renderedSizes = CanvasHelper.addText({
                canvas: canvasRef.current,
                text: item.content,
                color: item.color,
                x: item.x,
                y: item.y,
                fontSize: item.fontSize,
                font: item.font,
                textAlign: item.textAlign
              });

              if (renderedSizes && renderedSizes.w) item.renderedW = renderedSizes.w;
              if (renderedSizes && renderedSizes.h) item.renderedH = renderedSizes.h;

              item.w = TextPhotoDefaults.CANVAS_DEFAULT_WIDTH;
              item.h = TextPhotoDefaults.CANVAS_DEFAULT_HEIGHT;
            }
          }
        }
      }
    }

    setLayers(items);

    const canvas = canvasRef.current;
    const base64Final = canvas.toDataURL('image/jpeg', 0.8);
    setRenderedImage(base64Final);
  }, [setRenderedImage, setLayers]);

  const setFiles = useCallback(async (evt) => {
    const files = evt.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const FileDataUri = await FileHelper.readFile(file);

      const newLayers = layers;
      newLayers[0] = { id: LayerIdGenerator.generate(), type: 'image', file, dataUri: FileDataUri }

      draw(newLayers);
    }
  }, [layers, draw]);

  const addTextLayer = useCallback(async () => {
    let newLayers = layers;
    newLayers = [...newLayers, {
      id: LayerIdGenerator.generate(),
      type: 'text',
      content: Strings('your_text_here'),
      x: TextPhotoDefaults.CANVAS_DEFAULT_WIDTH / 2,
      y: TextPhotoDefaults.CANVAS_DEFAULT_HEIGHT / 2,
      color: TextPhotoDefaults.CANVAS_DEFAULT_TEXT_COLOR,
      fontSize: TextPhotoDefaults.CANVAS_DEFAULT_FONT_SIZE,
      font: TextPhotoDefaults.CANVAS_DEFAULT_FONT,
      textAlign: TextPhotoDefaults.CANVAS_DEFAULT_TEXT_ALIGN
    }];
    draw(newLayers);
  }, [layers, draw]);

  const updateTextLayerData = useCallback(async (id, property, value) => {
    if (property && value !== '') {
      const newLayers = layers;

      const numericalProperties = ['x', 'y', 'fontSize'];

      newLayers.filter(el => el && el.id === id).map((item) => {
        if (numericalProperties.includes(property)) {
          value = parseInt(value);
        }
        item[property] = value;
        return true;
      });

      draw(newLayers);
    }
  }, [layers, draw]);

  const updateMultipleTextLayerData = useCallback(async (items) => {
    const newLayers = layers;

    const numericalProperties = ['x', 'y', 'fontSize'];

    items.forEach(({id, property, value}) => {
      newLayers.filter(el => el && el.id === id).map((item) => {
        if (numericalProperties.includes(property)) {
          value = parseInt(value);
        }
        item[property] = value;
        return true;
      });
    });

    draw(newLayers);
  }, [layers, draw]);

  const removeTextLayer = useCallback((id) => {
    const currentLayers = layers;
    const newLayers = currentLayers.filter(el => el === null || (el && el.id !== id));
    draw(newLayers);
  }, [layers, draw]);

  const clearTextPhoto = useCallback(async () => {
    const newLayers = [];
    newLayers[0] = null;
    newLayers[1] = { id: LayerIdGenerator.generate(), type: 'shadow', color: CANVAS_DEFAULT_BACKGROUND_COLOR };

    draw(newLayers);
  }, [draw]);

  const triggerFilesPrompt = useCallback(() => {
    filesInput.current.click();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;

    canvas.width = CANVAS_DEFAULT_WIDTH;
    canvas.height = CANVAS_DEFAULT_HEIGHT;

    if (editing && editing.index !== undefined && editing.media) {
      const layersToSet = _.cloneDeep(editing.media.layers);
      draw(layersToSet);
      setRenderedImage(editing.media.src);
    } else {
      clearTextPhoto();
    }
  }, [clearTextPhoto, draw, editing]);

  const saveCallback = useCallback(() => {
    if (parentSaveCallback) {
      if (editing && editing.index !== undefined) {
        parentSaveCallback(layers, renderedImage, editing.index);
      } else {
        parentSaveCallback(layers, renderedImage);
      }
    }
    if (toggle) {
      toggle();
    }
  }, [parentSaveCallback, layers, renderedImage, editing, toggle]);

  return (
    <div>

      <Row>
        <Col>
          <canvas className='textphoto-canvas' ref={canvasRef} />
        </Col>
      </Row>

      <Row className='mt-1'>
        <Col>
          <Button className='mr-1' onClick={triggerFilesPrompt}>
            {Strings('background')}
          </Button>
          <Button className='mr-1' onClick={addTextLayer}>
            {Strings('add_text')}
          </Button>
          <Button className='mr-1' onClick={clearTextPhoto}>
            {Strings('clear')}
          </Button>
        </Col>
      </Row>

      <Row className='mt-2'>
        {layers && layers.length > 0 && layers.filter(el => el && el.type === 'text').map((item) => (
          <Col md={12} key={item.id} className='mb-3'>
            <Row>
              <Col md={8}>
                <Label className='mb-0'>{Strings('text_photo_label_text')}</Label>
                <Input
                  type='text'
                  defaultValue={item.content}
                  onBlur={(evt) => updateTextLayerData(item.id, 'content', evt.target.value)}
                  onClick={(evt) => {
                    const currentValue = evt.target.value;
                    if (currentValue === Strings('your_text_here')) {
                      evt.target.value = '';
                    }
                  }}
                />
              </Col>
              <Col md={2}>
                <Label className='mb-0'>{Strings('text_photo_label_font_size')}</Label>
                <Input
                  type='number'
                  defaultValue={item.fontSize}
                  onBlur={(evt) => updateTextLayerData(item.id, 'fontSize', evt.target.value)}
                />
              </Col>
              <Col md={2}>
                <Label className='mb-0'>{Strings('text_photo_label_delete')}</Label>
                <Button color='danger' size='lg' onClick={() => removeTextLayer(item.id)} className='d-block'>
                  <i className='pe-7s-trash'> </i>
                </Button>
              </Col>
            </Row>
            <Row className='mt-2'>
              <Col md={4}>
                <Label className='mb-0'>{Strings('text_photo_label_x')} <small>[{item.x}]</small></Label>
                <Slider
                  min={0}
                  max={TextPhotoDefaults.CANVAS_DEFAULT_WIDTH}
                  value={item && item.x !== 'undefined' && parseInt(item.x)}
                  defaultValue={item && item.x !== 'undefined' && parseInt(item.x)}
                  onChange={(value) => updateTextLayerData(item.id, 'x', value)}
                />
              </Col>
              <Col md={4}>
                <Label className='mb-0'>{Strings('text_photo_label_y')} <small>[{item.y}]</small></Label>
                <Slider
                  min={0}
                  max={TextPhotoDefaults.CANVAS_DEFAULT_HEIGHT}
                  value={item && item.y !== 'undefined' && parseInt(item.y)}
                  defaultValue={item && item.y !== 'undefined' && parseInt(item.y)}
                  onChange={(value) => updateTextLayerData(item.id, 'y', value)}
                />
              </Col>
              <Col md={4}>
                <Label className='mb-0'>{Strings('text_align')}</Label>
                <div>
                  <ButtonGroup size="sm" className="mb-2">
                    <Button
                      outline
                      className="btn-pill btn-dashed"
                      color="primary"
                      onClick={() => {
                        updateMultipleTextLayerData([
                          { id: item.id, property: 'textAlign', value: 'left' },
                          { id: item.id, property: 'x', value: TextPhotoDefaults.CANVAS_PHOTO_PADDING }
                        ]);
                      }}
                      active={item && item.textAlign === 'left'}
                    >
                      {Strings('text_align_left')}
                    </Button>
                    <Button
                      outline
                      className="btn-pill btn-dashed"
                      color="primary"
                      onClick={() => {
                        updateMultipleTextLayerData([
                          { id: item.id, property: 'textAlign', value: 'center' },
                          { id: item.id, property: 'x', value: TextPhotoDefaults.CANVAS_DEFAULT_WIDTH /2 }
                        ]);
                      }}
                      active={item && item.textAlign === 'center'}
                    >
                      {Strings('text_align_center')}
                    </Button>
                    <Button
                      outline
                      className="btn-pill btn-dashed"
                      color="primary"
                      onClick={() => {
                        updateMultipleTextLayerData([
                          { id: item.id, property: 'textAlign', value: 'right' },
                          { id: item.id, property: 'x', value: TextPhotoDefaults.CANVAS_DEFAULT_WIDTH - TextPhotoDefaults.CANVAS_PHOTO_PADDING }
                        ]);
                      }}
                      active={item && item.textAlign === 'right'}
                    >
                      {Strings('text_align_right')}
                    </Button>
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
          </Col>
        ))}
      </Row>

      <Row className='d-none'>
        <Col>
          <Input
            type='file'
            onChange={setFiles}
            innerRef={filesInput}
          />
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col>
          <Button color='primary' onClick={saveCallback}>
            {Strings('save')}
          </Button>
          <Button color='secondary ml-2' onClick={toggle}>
            {Strings('cancel')}
          </Button>
        </Col>
      </Row>

    </div>
  );
}
